import React from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Select,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import PlanPricingEditor from "../../../components/PlanPricingEditor";
import TopupCountryPricing from "../../../components/TopupCountryPricing";
import { topupAllowanceOptions } from "../../../constants/planConstants";
import styles from "./style";

const CreateNewTopupPage = ({
  isEditMode = false,
  topupId = "",
  form = {},
  errors = {},
  plans = [],
  pricingList = [],
  submitting = false,
  onChange,
  onToggleAllowance,
  onAllowanceValueChange,
  onPricingChange,
  onSubmit,
  onCancel,
}) => {
  const handle = (field) => (event) => onChange(field, event.target.value);

  const planOptions = plans.map((plan) => ({
    id: plan?._id || plan?.id,
    name: plan?.planName || plan?.name || "Untitled plan",
  }));

  const selectedPlanNames = (selected) =>
    selected.length === 0
      ? "All plans"
      : planOptions
          .filter((plan) => selected.includes(plan.id))
          .map((plan) => plan.name)
          .join(", ");

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        {isEditMode ? "Edit Topup Pack" : "Create New Topup"}
      </Typography>
      <Typography sx={styles.subtitle}>
        A topup raises one or more plan allowances without changing the
        customer&apos;s plan.
      </Typography>

      <Box sx={styles.card}>
        <Typography sx={styles.sectionTitle}>Pack details</Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography sx={styles.fieldLabel}>Name</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. 5 Extra Employees"
              value={form.name || ""}
              onChange={handle("name")}
              error={!!errors.name}
              helperText={errors.name || "What the customer sees."}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography sx={styles.fieldLabel}>What this pack grants</Typography>
            {isEditMode && (
              <Typography sx={styles.helperText}>
                Saving replaces this pack&apos;s entire allowance list — make sure
                every module it should still grant stays checked.
              </Typography>
            )}
            {errors.allowances && (
              <FormHelperText error>{errors.allowances}</FormHelperText>
            )}
            <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
              {topupAllowanceOptions.map((option) => {
                const included = Object.prototype.hasOwnProperty.call(
                  form.allowances || {},
                  option.value
                );
                const value = (form.allowances || {})[option.value];
                const fieldErrorKey = `allowance_${option.value}`;

                return (
                  <Grid item xs={12} md={6} key={option.value}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                      <Checkbox
                        size="small"
                        checked={included}
                        onChange={(e) =>
                          onToggleAllowance(option.value, e.target.checked)
                        }
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={styles.fieldLabel}>{option.label}</Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          disabled={!included}
                          value={value ?? ""}
                          onChange={(e) =>
                            onAllowanceValueChange(option.value, e.target.value)
                          }
                          error={!!errors[fieldErrorKey]}
                          helperText={
                            errors[fieldErrorKey] ||
                            (option.isAttendance
                              ? "Leave blank for unlimited attendance."
                              : "")
                          }
                          inputProps={option.isAttendance ? {} : { min: 1 }}
                        />
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography sx={styles.fieldLabel}>Pack type</Typography>
            <RadioGroup
              row
              value={form.packType || "fixed"}
              onChange={handle("packType")}
            >
              <FormControlLabel
                value="fixed"
                control={<Radio size="small" />}
                label="Fixed — a set bundle"
              />
              <FormControlLabel
                value="per_unit"
                control={<Radio size="small" />}
                label="Per-unit — customer chooses how many"
              />
            </RadioGroup>
            {errors.packType && (
              <FormHelperText error>{errors.packType}</FormHelperText>
            )}
          </Grid>

          {form.packType === "per_unit" && (
            <>
              <Grid item xs={12} md={6}>
                <Typography sx={styles.fieldLabel}>Minimum quantity</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.minQuantity ?? ""}
                  onChange={handle("minQuantity")}
                  error={!!errors.minQuantity}
                  helperText={errors.minQuantity}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography sx={styles.fieldLabel}>Maximum quantity</Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={form.maxQuantity ?? ""}
                  onChange={handle("maxQuantity")}
                  error={!!errors.maxQuantity}
                  helperText={errors.maxQuantity}
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} md={6}>
            <Typography sx={styles.fieldLabel}>Available to</Typography>
            <Select
              multiple
              fullWidth
              size="small"
              displayEmpty
              value={form.availableToPlans || []}
              onChange={(event) => onChange("availableToPlans", event.target.value)}
              input={<OutlinedInput />}
              renderValue={selectedPlanNames}
            >
              {planOptions.map((plan) => (
                <MenuItem key={plan.id} value={plan.id}>
                  <Checkbox
                    size="small"
                    checked={(form.availableToPlans || []).includes(plan.id)}
                  />
                  <ListItemText primary={plan.name} />
                </MenuItem>
              ))}
            </Select>
            <Typography sx={styles.helperText}>
              Leave empty to offer this pack on all plans.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={styles.card}>
        {isEditMode ? (
          // Edit mode manages pricing live through the dedicated pricing
          // endpoints — it is never sent through PUT /topup/:id.
          <TopupCountryPricing topupId={topupId} />
        ) : (
          <>
            <Typography sx={styles.sectionTitle}>Country pricing</Typography>
            <PlanPricingEditor value={pricingList} onChange={onPricingChange} />
            {errors.pricing && (
              <FormHelperText error>{errors.pricing}</FormHelperText>
            )}
          </>
        )}
      </Box>

      <Box sx={styles.actions}>
        <Button variant="outlined" onClick={onCancel} sx={styles.cancelButton}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onSubmit}
          disabled={submitting}
          sx={styles.saveButton}
        >
          {isEditMode ? "Save Changes" : "Create Topup"}
        </Button>
      </Box>
    </Box>
  );
};

export default CreateNewTopupPage;
